import { createRoute } from "@tanstack/solid-router";
import { Link, useNavigate } from "@/lib/u-router";
import { appRoute } from "./route";
import { createEffect, createSignal, For, onMount, Show, untrack } from "solid-js";
import { BookOpen, ChevronDown, ChevronUp, Filter, Search as SearchIcon, X } from "lucide-solid";
import { bibleBooks, bookGroups, getBookNameDescriptor, idToAbbr } from "@/utils/bible-books-data";
import { msg } from "@lingui/core/macro";
import { useI18n } from "@/providers/i18n-provider";
import { api } from "@/lib/api";
import { EmptyIllustration } from "@/components/ui/empty-illustration";
import { getLastReadBible } from "@/utils/last-read";
import type { components } from "@bso/client";

type VerseResponse = components["schemas"]["VerseResponse"];
type SearchCursor = components["schemas"]["SearchCursor"];

// Fallback version when the URL doesn't pin one: the version the user is
// currently reading (legacy parity: the searcher used the active bible version).
function getActiveVersion(): string {
  return getLastReadBible().version;
}

const VERSION_PATTERN = /^[a-z0-9]{2,16}$/i;

/** Saneo de `?v=` (abreviatura de versión) a minúsculas; omite valores basura. */
function parseVersionParam(value: unknown): string | undefined {
  if (typeof value !== "string") {
    return undefined;
  }
  const v = value.trim().toLowerCase();
  return VERSION_PATTERN.test(v) ? v : undefined;
}

/**
 * Cursor de paginación serializado para la URL (`?cursor=book.chapter.verse`).
 * Hace la página de resultados **reproducible**: pegar la URL reanuda donde el
 * usuario estaba. Devuelve `null` ante valores incompletos/basura. La versión no
 * va en este string (vive en `?v=`); el caller la inyecta al reanudar.
 */
function parseCursorParam(value: unknown, version: string): SearchCursor | null {
  if (typeof value !== "string") {
    return null;
  }
  const parts = value.split(".");
  if (parts.length !== 3) {
    return null;
  }
  const book = Number.parseInt(parts[0] ?? "", 10);
  const chapter = Number.parseInt(parts[1] ?? "", 10);
  const verse = Number.parseInt(parts[2] ?? "", 10);
  if (!Number.isInteger(book) || !Number.isInteger(chapter) || !Number.isInteger(verse)) {
    return null;
  }
  return { book, chapter, verse, version };
}

/** ¿Es `?cursor=` sintácticamente válido? (saneo en validateSearch, sin versión). */
function isValidCursorString(value: unknown): boolean {
  return parseCursorParam(value, "") !== null;
}

function serializeCursor(cursor: SearchCursor | null): string | undefined {
  if (!cursor) {
    return undefined;
  }
  return `${cursor.book}.${cursor.chapter}.${cursor.verse}`;
}

/** Parsea el parámetro `books` (CSV de ids) a una lista numérica saneada. */
function parseBooks(value: string | undefined): number[] {
  if (!value) {
    return [];
  }
  return value
    .split(",")
    .map((part) => Number.parseInt(part.trim(), 10))
    .filter((n) => Number.isInteger(n) && n > 0);
}

function escapeRegExp(string: string) {
  return string.replaceAll(/[.*+?^${}()|[\]\\]/g, String.raw`\$&`);
}

function highlightMatch(text: string, query: string) {
  const cleanText = text.replaceAll(/<W[GH]\d+>/g, "");
  if (!query.trim()) {
    return <span>{cleanText}</span>;
  }

  try {
    const parts = cleanText.split(new RegExp(`(${escapeRegExp(query)})`, "gi"));
    return (
      <For each={parts}>
        {(part) => {
          if (part.toLowerCase() === query.toLowerCase()) {
            return (
              <mark class="bg-primary/20 text-primary dark:text-primary-foreground font-semibold px-0.5 rounded">
                {part}
              </mark>
            );
          }
          return <span>{part}</span>;
        }}
      </For>
    );
  } catch {
    return <span>{cleanText}</span>;
  }
}

function SearchPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const search = searchRoute.useSearch();

  // El término, el modo de concordancia y los libros filtrados viven en la URL
  // (`?q=&t=&books=`) para que una búsqueda sea **compartible/recargable**: al
  // cargar por URL se reconstruye el estado y se relanza la búsqueda. Los signals
  // locales son la fuente reactiva de render, inicializados desde la URL.
  const [searchText, setSearchText] = createSignal(search().q ?? "");
  const [selectedBooks, setSelectedBooks] = createSignal<number[]>(parseBooks(search().books));
  const [items, setItems] = createSignal<VerseResponse[]>([]);
  const [nextCursor, setNextCursor] = createSignal<SearchCursor | null>(null);
  const [hasMore, setHasMore] = createSignal(false);
  const [loading, setLoading] = createSignal(false);
  const [showFilters, setShowFilters] = createSignal(parseBooks(search().books).length > 0);
  const [concordance, setConcordance] = createSignal<"EXACT" | "FUZZY">(
    search().t === "EXACT" ? "EXACT" : "FUZZY",
  );
  const [bookCounts, setBookCounts] = createSignal<Map<number, number> | null>(null);

  // Versión usada en la búsqueda. La URL (`?v=`) la fija de forma **determinista**
  // para que la misma URL dé los mismos resultados a cualquiera (gap #24): sin el
  // param se cae a la última versión leída, pero al buscar se ancla en la URL.
  const searchVersion = (): string => parseVersionParam(search().v) ?? getActiveVersion();

  // Escribe el estado de búsqueda compartible a la URL (idempotente, sin inflar
  // el historial). Se llama tras cada cambio que afecta QUÉ se busca. `cursorOverride`
  // permite anclar la página actual (`?cursor=`) al cargar más resultados.
  function syncUrl(cursorOverride?: SearchCursor | null) {
    const q = searchText().trim();
    const books = selectedBooks();
    void navigate({
      to: "/search",
      search: {
        q: q === "" ? undefined : q,
        t: concordance() === "EXACT" ? "EXACT" : undefined,
        books: books.length > 0 ? books.join(",") : undefined,
        // La versión se ancla en la URL solo cuando hay término (búsqueda real),
        // para no ensuciar la URL vacía con la última-lectura del visitante.
        v: q === "" ? undefined : searchVersion(),
        cursor: cursorOverride === undefined ? search().cursor : serializeCursor(cursorOverride),
      },
      replace: true,
    });
  }

  // URL → signals (back/forward + enlace pegado). El valor de la URL manda.
  // La comparación va en `untrack` para que el efecto SOLO reaccione a cambios
  // EXTERNOS de la URL; si trackea `searchText()` (la señal que escribe), un
  // cambio state-first re-dispara el efecto con la URL vieja y revierte el
  // input (bug #15b / CC-16, ya arreglado en font-manager.tsx).
  createEffect(() => {
    const urlQuery = search().q ?? "";
    if (untrack(() => urlQuery !== searchText())) {
      setSearchText(urlQuery);
    }
  });

  const totalResults = () => {
    const counts = bookCounts();
    if (!counts) {
      return null;
    }
    let total = 0;
    for (const count of counts.values()) {
      total += count;
    }
    return total;
  };

  // Per-book result counts (legacy parity: use-bible-search-books-result-count)
  async function fetchBookCounts(text: string) {
    try {
      const { data, response } = await api.GET("/api/v1/bible/verses/search-count", {
        params: {
          query: {
            version: searchVersion(),
            text,
            t: concordance(),
            books: null,
          },
        },
      });
      if (response.ok && data) {
        const map = new Map<number, number>();
        for (const row of data) {
          map.set(row.book, row.count);
        }
        setBookCounts(map);
      }
    } catch (e) {
      console.warn("Failed to fetch per-book result counts", e);
    }
  }

  const toggleCategory = (ids: number[]) => {
    const allSelected = ids.every((id) => selectedBooks().includes(id));
    if (allSelected) {
      setSelectedBooks((prev) => prev.filter((id) => !ids.includes(id)));
    } else {
      setSelectedBooks((prev) => {
        const base = prev.filter((id) => !ids.includes(id));
        return [...base, ...ids];
      });
    }
    syncUrl(null);
  };

  const handleSearch = async (reset = true, cursorOverride?: SearchCursor | null) => {
    if (!searchText().trim()) {
      return;
    }

    // `reset` arranca en página 1 (limpia el cursor de la URL). `cursorOverride`
    // (deep-link `?cursor=`) reanuda en esa página. Si no, usamos el cursor en mano.
    const currentCursor = reset ? null : (cursorOverride ?? nextCursor());

    if (reset) {
      syncUrl(null);
    } else {
      syncUrl(currentCursor);
    }
    setLoading(true);
    try {
      const booksParam = selectedBooks().length > 0 ? selectedBooks().join(",") : null;

      if (reset) {
        setBookCounts(null);
        void fetchBookCounts(searchText());
      }

      const { data, response } = await api.GET("/api/v1/bible/verses/search", {
        params: {
          query: {
            version: searchVersion(),
            text: searchText(),
            t: concordance(),
            books: booksParam,
            book: currentCursor?.book ?? null,
            chapter: currentCursor?.chapter ?? null,
            verse: currentCursor?.verse ?? null,
            cursorDirection: currentCursor ? "NEXT" : null,
          },
        },
      });

      if (!response.ok) {
        console.error("Failed to search verses");
        return;
      }

      if (data) {
        if (reset) {
          setItems(data.items);
        } else {
          setItems((prev) => [...prev, ...data.items]);
        }
        setNextCursor(data.nextCursor ?? null);
        setHasMore(data.hasNextPage);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const toggleBookFilter = (id: number) => {
    if (selectedBooks().includes(id)) {
      setSelectedBooks((prev) => prev.filter((b) => b !== id));
    } else {
      setSelectedBooks((prev) => [...prev, id]);
    }
    syncUrl(null);
  };

  const clearFilters = () => {
    setSelectedBooks([]);
    syncUrl(null);
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Enter") {
      void handleSearch(true);
    }
  };

  // Deep-link: si la URL trae un término, relanza la búsqueda al montar para
  // reconstruir los resultados (idempotente al recargar/pegar el enlace). Si la
  // URL trae también un `?cursor=`, reanuda en esa página en lugar de la 1.
  onMount(() => {
    if (searchText().trim()) {
      const bootCursor = parseCursorParam(search().cursor, searchVersion());
      void (bootCursor ? handleSearch(false, bootCursor) : handleSearch(true));
    }
  });

  return (
    <div class="flex flex-col h-full w-full bg-background text-foreground overflow-hidden">
      {/* Header */}
      <div class="border-b border-border/40 bg-card/30 backdrop-blur-lg px-4 md:px-8 py-4 md:py-5 flex items-center justify-between shrink-0">
        <div class="flex items-center gap-3">
          <div class="p-2.5 rounded-xl bg-primary/10 border border-primary/20 text-primary">
            <SearchIcon class="h-5 w-5" />
          </div>
          <div>
            <h1 class="text-xl font-bold tracking-tight bg-gradient-to-r from-foreground to-foreground/75 bg-clip-text text-transparent">
              {t(msg`Bible Search`)}
            </h1>
            <p class="text-xs text-muted-foreground mt-0.5">
              {t(msg`Fast full-text search (FTS)`)}
            </p>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div class="flex-1 overflow-y-auto p-4 md:p-8 max-w-4xl mx-auto w-full flex flex-col space-y-6 pb-24">
        {/* Search controls */}
        <div class="space-y-4 bg-card/40 border border-border/30 rounded-2xl p-5 shadow-sm">
          <div class="flex gap-3">
            <div class="relative flex-1">
              <SearchIcon class="absolute left-4 top-1/2 -translate-y-1/2 h-4.5 w-4.5 text-muted-foreground" />
              <input
                type="text"
                placeholder={t(msg`Search by word or phrase (e.g.: 'grace', 'beginning')...`)}
                value={searchText()}
                onInput={(e) => setSearchText(e.target.value)}
                onKeyDown={handleKeyDown}
                class="w-full pl-11 pr-4 py-3 bg-background border border-border/50 rounded-xl text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all"
              />
            </div>
            <button
              type="button"
              onClick={() => {
                void handleSearch(true);
              }}
              disabled={loading() || !searchText().trim()}
              class="px-6 py-3 bg-primary text-primary-foreground hover:bg-primary/95 disabled:opacity-50 text-sm font-semibold rounded-xl transition-all shadow-md active:scale-98"
            >
              {loading() && items().length === 0 ? t(msg`Searching...`) : t(msg`Search`)}
            </button>
          </div>

          {/* Concordance type controller */}
          <div class="flex items-center gap-3 border-t border-border/20 pt-3">
            <span class="text-2xs font-semibold text-muted-foreground">{t(msg`Concordance:`)}</span>
            <div class="flex bg-muted/40 border border-border/30 p-0.5 rounded-lg shrink-0">
              <button
                type="button"
                onClick={() => {
                  setConcordance("FUZZY");
                  syncUrl(null);
                }}
                class="px-3 py-1 text-3xs font-bold rounded-md transition-all"
                classList={{
                  "bg-primary text-primary-foreground shadow-xs": concordance() === "FUZZY",
                  "text-muted-foreground hover:text-foreground": concordance() !== "FUZZY",
                }}
              >
                {t(msg`Partial (Fuzzy)`)}
              </button>
              <button
                type="button"
                onClick={() => {
                  setConcordance("EXACT");
                  syncUrl(null);
                }}
                class="px-3 py-1 text-3xs font-bold rounded-md transition-all"
                classList={{
                  "bg-primary text-primary-foreground shadow-xs": concordance() === "EXACT",
                  "text-muted-foreground hover:text-foreground": concordance() !== "EXACT",
                }}
              >
                {t(msg`Exact (Word)`)}
              </button>
            </div>
          </div>

          {/* Collapsible Book Filters */}
          <div class="border-t border-border/30 pt-3">
            <button
              type="button"
              onClick={() => setShowFilters(!showFilters())}
              class="flex items-center justify-between w-full py-1.5 px-2.5 rounded-lg text-xs font-semibold text-muted-foreground hover:bg-muted/30 transition-colors"
            >
              <div class="flex items-center gap-2">
                <Filter class="h-3.5 w-3.5" />
                <span>
                  {t(msg`Filter by books`)}{" "}
                  {selectedBooks().length > 0
                    ? `(${selectedBooks().length} ${t(msg`selected`)})`
                    : ""}
                </span>
              </div>
              <Show when={showFilters()} fallback={<ChevronDown class="h-3.5 w-3.5" />}>
                <ChevronUp class="h-3.5 w-3.5" />
              </Show>
            </button>

            <Show when={showFilters()}>
              <div class="mt-3 space-y-4">
                {/* Canonical Groups */}
                <div class="space-y-2 border-b border-border/20 pb-3">
                  <span class="text-2xs text-muted-foreground font-semibold">
                    {t(msg`Advanced Canonical Groups:`)}
                  </span>
                  <div class="flex flex-wrap gap-1.5">
                    <For each={bookGroups}>
                      {(group) => {
                        const isAllSelected = () =>
                          group.ids.every((id) => selectedBooks().includes(id));
                        return (
                          <button
                            type="button"
                            onClick={() => {
                              toggleCategory(group.ids);
                            }}
                            class="px-2.5 py-1 text-3xs font-bold border rounded-lg transition-all"
                            classList={{
                              "bg-primary text-primary-foreground border-primary shadow-xs":
                                isAllSelected(),
                              "bg-background hover:bg-muted/40 border-border/40 text-foreground/80":
                                !isAllSelected(),
                            }}
                          >
                            {t(group.name)}
                          </button>
                        );
                      }}
                    </For>
                  </div>
                </div>

                <div class="flex items-center justify-between border-b border-border/20 pb-2">
                  <span class="text-2xs text-muted-foreground font-medium">
                    {t(msg`Select one or more books individually`)}
                  </span>
                  <Show when={selectedBooks().length > 0}>
                    <button
                      type="button"
                      onClick={clearFilters}
                      class="text-2xs font-semibold text-primary hover:underline flex items-center gap-1"
                    >
                      <X class="w-3 h-3" />
                      <span>{t(msg`Clear filters`)}</span>
                    </button>
                  </Show>
                </div>
                <div class="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 gap-2 max-h-48 overflow-y-auto p-1">
                  <For each={bibleBooks}>
                    {(book) => (
                      <button
                        type="button"
                        onClick={() => {
                          toggleBookFilter(book.id);
                        }}
                        class="px-2 py-1.5 text-3xs font-medium border rounded-lg text-left transition-all truncate flex items-center justify-between gap-1"
                        classList={{
                          "bg-primary/10 border-primary text-primary": selectedBooks().includes(
                            book.id,
                          ),
                          "bg-background hover:bg-muted/50 border-border/40 text-foreground/80":
                            !selectedBooks().includes(book.id),
                        }}
                      >
                        <span class="truncate">{t(book.name)}</span>
                        <Show when={(bookCounts()?.get(book.id) ?? 0) > 0}>
                          <span class="shrink-0 px-1 rounded-md bg-primary/15 text-primary font-bold text-4xs">
                            {bookCounts()?.get(book.id)}
                          </span>
                        </Show>
                      </button>
                    )}
                  </For>
                </div>
              </div>
            </Show>
          </div>
        </div>

        {/* Results bar (legacy parity: search-results-bar with total count) */}
        <Show when={totalResults() !== null && items().length > 0}>
          <div class="flex items-center justify-between px-4 py-2.5 rounded-xl bg-muted/20 border border-border/30">
            <span class="text-xs font-semibold text-foreground/85">
              {totalResults()} {totalResults() === 1 ? t(msg`result`) : t(msg`results`)}{" "}
              {t(msg`for`)} «{searchText().trim()}»
            </span>
            <span class="text-3xs font-bold uppercase tracking-wide text-muted-foreground">
              {concordance() === "EXACT" ? t(msg`Exact`) : t(msg`Partial`)} ·{" "}
              {searchVersion().toUpperCase()}
            </span>
          </div>
        </Show>

        {/* Results List */}
        <div class="space-y-4">
          <Show
            when={items().length > 0}
            fallback={
              <Show when={!loading()}>
                <div class="flex flex-col items-center justify-center py-16 border border-dashed border-border/40 rounded-2xl bg-card/10 text-center p-6 motion-safe:animate-fade-in">
                  <EmptyIllustration variant="search" class="mb-3" />
                  <h3 class="text-sm font-bold text-foreground/80">{t(msg`Start your search`)}</h3>
                  <p class="text-xs text-muted-foreground max-w-sm mt-1">
                    {t(msg`Enter terms, keywords or complete phrases to find verses instantly.`)}
                  </p>
                </div>
              </Show>
            }
          >
            <div class="space-y-3">
              <For each={items()}>
                {(item) => (
                  <Link
                    to="/bible/$version/$bookAbbr/$chapter"
                    params={{
                      version: item.version,
                      bookAbbr: idToAbbr(item.book),
                      chapter: String(item.chapter),
                    }}
                    search={{
                      verse: item.verse,
                    }}
                    class="block p-5 bg-card/30 border border-border/30 hover:border-primary/40 rounded-xl transition-all shadow-2xs hover:shadow-sm"
                  >
                    <div class="flex items-center justify-between border-b border-border/20 pb-2 mb-3">
                      <div class="flex items-center gap-2">
                        <BookOpen class="w-3.5 h-3.5 text-primary" />
                        <span class="text-xs font-bold text-foreground/80">
                          {t(getBookNameDescriptor(item.book))} {item.chapter}:{item.verse}
                        </span>
                      </div>
                      <span class="text-3xs px-2 py-0.5 rounded bg-primary/10 border border-primary/20 text-primary uppercase font-bold tracking-wider">
                        {item.version}
                      </span>
                    </div>
                    <p class="text-sm leading-relaxed text-foreground/85 font-serif">
                      {highlightMatch(item.text, searchText())}
                    </p>
                  </Link>
                )}
              </For>
            </div>

            {/* Load More Button */}
            <Show when={hasMore()}>
              <div class="pt-4 flex justify-center">
                <button
                  type="button"
                  onClick={() => {
                    void handleSearch(false);
                  }}
                  disabled={loading()}
                  class="px-6 py-2.5 bg-muted/60 hover:bg-muted text-foreground/85 border border-border/40 text-xs font-semibold rounded-lg shadow-2xs transition-all flex items-center gap-2 disabled:opacity-50 active:scale-98"
                >
                  <Show when={loading()} fallback={<span>{t(msg`Load more results`)}</span>}>
                    <div class="w-3.5 h-3.5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                    <span>{t(msg`Loading...`)}</span>
                  </Show>
                </button>
              </div>
            </Show>
          </Show>

          {/* Loading Skeletal state */}
          <Show when={loading() && items().length === 0}>
            <div class="space-y-3 animate-pulse">
              <div class="h-24 bg-card/40 border border-border/20 rounded-xl" />
              <div class="h-24 bg-card/40 border border-border/20 rounded-xl" />
              <div class="h-24 bg-card/40 border border-border/20 rounded-xl" />
            </div>
          </Show>
        </div>
      </div>
    </div>
  );
}

type SearchParams = {
  /** Término de búsqueda. */
  q?: string;
  /** Modo de concordancia: `EXACT` (palabra exacta) o ausente (fuzzy/parcial). */
  t?: "EXACT";
  /** Libros filtrados, CSV de ids (p.ej. `1,2,40`). */
  books?: string;
  /** Versión bíblica anclada para resultados deterministas (p.ej. `kjv`). */
  v?: string;
  /** Cursor de paginación `book.chapter.verse` para reanudar en una página. */
  cursor?: string;
};

export const searchRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/search",
  validateSearch: (search: Record<string, unknown>): SearchParams => ({
    q: typeof search.q === "string" && search.q.trim() !== "" ? search.q : undefined,
    t: search.t === "EXACT" ? "EXACT" : undefined,
    books:
      typeof search.books === "string" && parseBooks(search.books).length > 0
        ? parseBooks(search.books).join(",")
        : undefined,
    v: parseVersionParam(search.v),
    // Conservamos el `?cursor=` solo si es sintácticamente válido; la versión se
    // resuelve en runtime (`?v=`/última-lectura), no en este saneo puro.
    cursor:
      typeof search.cursor === "string" && isValidCursorString(search.cursor)
        ? search.cursor
        : undefined,
  }),
  component: SearchPage,
});
